/**
 * AB Test UUID Manager
 *
 * Manages experiment-scoped UUIDs for anonymous users participating in AB tests.
 * Each AB test gets its own UUID that persists in localStorage and expires based
 * on the test's end date.
 *
 * Key Format: P13N-UUID-<AB_TEST_ID>
 */

import Storage from 'utils/localStorage/Storage';
import UUIDv4 from 'utils/UUID';

const { local, DAYS } = Storage;

/**
 * Default TTL for AB test UUIDs when no end date is provided (7 days)
 */
const DEFAULT_TTL = 7 * DAYS;

/**
 * Storage key prefix for AB test UUIDs
 */
const STORAGE_KEY_PREFIX = 'P13N-UUID-';

/**
 * Generates a storage key for a given AB test ID
 * @param {string} abTestId - The AB test identifier
 * @returns {string} The storage key in format P13N-UUID-<AB_TEST_ID>
 */
function getStorageKey(abTestId) {
    if (!abTestId) {
        throw new Error('AB Test ID is required');
    }

    return `${STORAGE_KEY_PREFIX}${abTestId}`;
}

/**
 * Calculates expiration time based on AB test end date
 * @param {string} abTestEndDate - ISO 8601 date string (e.g., "2026-03-27T11:11-07:00")
 * @returns {Date|number} Date object for expiration or default TTL in ms
 */
function calculateExpiration(abTestEndDate) {
    if (!abTestEndDate) {
        return DEFAULT_TTL;
    }

    try {
        const endDate = new Date(abTestEndDate);

        // Validate the date
        if (isNaN(endDate.getTime())) {
            Sephora.logger.warn(`Personalization: Invalid AB test end date: ${abTestEndDate}. Using default TTL.`);

            return DEFAULT_TTL;
        }

        // Check if the end date is in the past
        if (endDate.getTime() < Date.now()) {
            Sephora.logger.warn(`Personalization: AB test end date is in the past: ${abTestEndDate}. UUID will expire immediately.`);

            return new Date(Date.now() + 1000); // Expire in 1 second
        }

        return endDate;
    } catch (error) {
        Sephora.logger.warn(`Personalization: Error parsing AB test end date: ${abTestEndDate}. Using default TTL.`, error);

        return DEFAULT_TTL;
    }
}

/**
 * Gets or creates a UUID for a specific AB test
 * @param {string} abTestId - The AB test identifier
 * @param {string} abTestEndDate - Optional ISO 8601 date string for expiration
 * @returns {string} The UUID for the AB test
 */
function getOrCreateAbTestUuid(abTestId, abTestEndDate = null) {
    if (!abTestId) {
        Sephora.logger.error('Personalization: AB Test ID is required for UUID generation');

        return null;
    }

    const storageKey = getStorageKey(abTestId);

    // Try to retrieve existing UUID (with automatic expiration check)
    const existingUuid = local.getItem(storageKey, false, true);

    if (existingUuid) {
        Sephora.logger.verbose(`Personalization: Retrieved existing UUID for AB test ${abTestId}: ${existingUuid}`);

        return existingUuid;
    }

    // Generate new UUID
    const newUuid = UUIDv4();
    const expiration = calculateExpiration(abTestEndDate);

    // Store with expiration
    local.setItem(storageKey, newUuid, expiration);

    Sephora.logger.verbose(`Personalization: Generated new UUID for AB test ${abTestId}: ${newUuid}`);

    return newUuid;
}

/**
 * Removes UUID for a specific AB test
 * @param {string} abTestId - The AB test identifier
 */
function removeAbTestUuid(abTestId) {
    if (!abTestId) {
        return;
    }

    const storageKey = getStorageKey(abTestId);
    local.removeItem(storageKey);

    Sephora.logger.verbose(`Personalization: Removed UUID for AB test ${abTestId}`);
}

/**
 * Cleans up all expired AB test UUIDs from localStorage
 */
function cleanupExpiredAbTestUuids() {
    try {
        let cleanedCount = 0;

        // Iterate through localStorage and remove expired P13N-UUID-* keys
        for (const key in window.localStorage) {
            if (key.startsWith(STORAGE_KEY_PREFIX)) {
                // getItem with removeExpired=true will auto-remove if expired
                const value = local.getItem(key, false, true);

                if (value === null) {
                    cleanedCount++;
                }
            }
        }

        if (cleanedCount > 0) {
            Sephora.logger.verbose(`Personalization: Cleaned up ${cleanedCount} expired AB test UUIDs`);
        }
    } catch (error) {
        Sephora.logger.error('Personalization: Error during AB test UUID cleanup', error);
    }
}

/**
 * Gets all active AB test UUIDs
 * @returns {Object} Map of AB test IDs to their UUIDs
 */
function getAllAbTestUuids() {
    const abTestUuids = {};

    try {
        for (const key in window.localStorage) {
            if (key.startsWith(STORAGE_KEY_PREFIX)) {
                const uuid = local.getItem(key, false, true);

                if (uuid) {
                    const abTestId = key.replace(STORAGE_KEY_PREFIX, '');
                    abTestUuids[abTestId] = uuid;
                }
            }
        }
    } catch (error) {
        Sephora.logger.error('Personalization: Error retrieving AB test UUIDs', error);
    }

    return abTestUuids;
}

export {
    getOrCreateAbTestUuid, removeAbTestUuid, cleanupExpiredAbTestUuids, getAllAbTestUuids
};
